import { type ReactElement } from 'react';
import { cn } from '@/lib/cn';
import { Icon, type IconName } from '@/components/atoms/icon';

type Props = {
  icon: IconName;
  label: string;
  description: string;
  selected: boolean;
  onSelect: () => void;
};

function handleKeyActivation(event: React.KeyboardEvent, onSelect: () => void): void {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    onSelect();
  }
}

export function PriorityOptionRow({ icon, label, description, selected, onSelect }: Props): ReactElement {
  return (
    <div
      role="radio"
      aria-checked={selected}
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => handleKeyActivation(e, onSelect)}
      className={cn(
        'flex items-center gap-4 rounded-[var(--r-sm)] border p-4 cursor-pointer transition-all',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]',
        'focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg-surface)]',
        selected
          ? 'border-[var(--brand)] bg-[var(--brand)]/5 text-[var(--ink)]'
          : 'border-[var(--rule)] bg-[var(--bg-surface)] text-[var(--ink)] hover:border-[var(--brand)]',
      )}
    >
      <Icon name={icon} size={20} className="shrink-0 text-[var(--ink-2)]" />
      <div className="flex flex-col gap-0.5">
        <span className="text-sm font-semibold">{label}</span>
        <span className="text-xs text-[var(--ink-3)]">{description}</span>
      </div>
    </div>
  );
}
